// Premium Services Overview - Gallery treatment with distinctive signatures
import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Smartphone, Zap, Globe } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const services = [
  {
    icon: Code,
    title: "Web Apps",
    benefit: "Scalable applications built with modern frameworks",
    description: "Custom web applications that grow with your business needs",
    href: "/services/web-app-development"
  },
  {
    icon: Smartphone,
    title: "Mobile Apps", 
    benefit: "Native iOS and Android experiences",
    description: "Cross-platform mobile solutions with native performance",
    href: "/services/mobile-app-development"
  },
  {
    icon: Zap,
    title: "SaaS MVPs",
    benefit: "Rapid prototyping and market validation",
    description: "Launch your SaaS idea with proven architecture",
    href: "/services/custom-software"
  },
  {
    icon: Globe,
    title: "Website-as-a-Service",
    benefit: "Professional websites with everything included",
    description: "Managed hosting, SEO, and ongoing maintenance",
    href: "/services/website-development"
  }
];

export const ServicesOverview = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="section-professional">
      <div className="container-professional">
        <div className="section-header-professional">
          <div className="micro-label-professional">
            WHAT WE BUILD
          </div>
          <h2 className="heading-section">
            <span className="accent-keyword">Web</span> and product capabilities
          </h2>
          <p className="text-body">
            Full-stack solutions that drive measurable business growth
          </p>
        </div>

        <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            
            return (
              <Link
                key={service.title}
                to={service.href}
                className="group relative"
                style={getRevealStyle(index)}
              >
                <div 
                  className="relative h-full rounded-xl p-12 transition-all duration-200 overflow-hidden"
                  style={{
                    background: '#111111',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '12px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  }}
                >
                  {/* Accent line - ALWAYS visible on hover */}
                  <div 
                    className="absolute top-0 left-0 right-0 h-[3px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{ 
                      background: 'linear-gradient(90deg, transparent, hsl(194 100% 50%), transparent)',
                      boxShadow: '0 0 24px hsl(194 100% 50% / 0.6)',
                    }}
                  ></div>
                  
                  {/* Icon - Clean and simple */}
                  <div className="mb-6">
                    <Icon className="w-10 h-10" strokeWidth={1.5} style={{ color: '#00e5ff' }} />
                  </div>
                  
                  {/* Title - pure white */}
                  <h3 
                    className="text-4xl font-bold mb-4"
                    style={{ color: '#FFFFFF' }}
                  >
                    {service.title}
                  </h3>
                  
                  <p 
                    className="text-lg leading-relaxed mb-4"
                    style={{ color: 'hsl(var(--text-secondary))' }}
                  >
                    {service.benefit}
                  </p>
                  
                  <div 
                    className="flex items-center gap-2 font-medium mt-6 group-hover:translate-x-1 transition-transform duration-300"
                    style={{ color: 'hsl(var(--primary))' }}
                  >
                    <span>Learn more</span>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};